<script module>
    export { default as layout } from '../layouts/AppLayout.svelte';
</script>

<script lang="ts">
    import { Link, useForm, page } from '@inertiajs/svelte';
    import { store } from '@/routes/feedback';
    import { home } from '@/routes';

    const user = $derived($page?.props?.auth?.user);
    const turnstileSiteKey: string | null = $derived($page?.props?.turnstileSiteKey as string | null);
    const showCaptcha = $derived(!user && !!turnstileSiteKey);
    let submitted = $state(false);

    const form = useForm({
        name: user?.name ?? '',
        email: user?.email ?? '',
        message: '',
        cf_turnstile_response: '',
    });

    let turnstileContainer: HTMLDivElement | undefined = $state();
    let turnstileWidgetId: string | undefined = $state();

    function renderTurnstile(): void {
        if (!turnstileContainer || !turnstileSiteKey || !window.turnstile) {
            return;
        }
        if (turnstileWidgetId !== undefined) {
            window.turnstile.reset(turnstileWidgetId);
            return;
        }
        turnstileWidgetId = window.turnstile.render(turnstileContainer, {
            sitekey: turnstileSiteKey,
            callback: (token: string) => { $form.cf_turnstile_response = token; },
            'expired-callback': () => { $form.cf_turnstile_response = ''; },
            'error-callback': () => { $form.cf_turnstile_response = ''; },
        });
    }

    $effect(() => {
        if (!showCaptcha || !turnstileContainer) {
            return;
        }

        if (window.turnstile) {
            renderTurnstile();
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onTurnstileLoad';
        script.async = true;
        (window as any).onTurnstileLoad = renderTurnstile;
        document.head.appendChild(script);

        return () => {
            delete (window as any).onTurnstileLoad;
        };
    });

    function submit(e: Event) {
        e.preventDefault();
        $form.post(store(), {
            preserveScroll: true,
            onSuccess: () => {
                submitted = true;
            },
        });
    }
</script>

<svelte:head>
    <title>Feedback — Case of the Day</title>
</svelte:head>

<div class="max-w-2xl mx-auto">
        <h1 class="text-3xl font-bold text-gray-100 mb-6">Feedback</h1>

        <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-6">
            {#if submitted}
                <div class="text-center py-8">
                    <svg class="mx-auto size-12 text-green-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                    </svg>
                    <h2 class="text-xl font-semibold text-gray-100 mb-2">Thank you for your feedback!</h2>
                    <p class="text-gray-400 mb-6">We appreciate you taking the time to reach out. We'll review your message shortly.</p>
                    <Link href={home()} class="inline-flex items-center rounded-md bg-cyan-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-cyan-500">&larr; Back to Case of the Day</Link>
                </div>
            {:else}
                <p class="text-gray-400 mb-6">Have a question, suggestion, or found a bug? Let us know.</p>

                <form onsubmit={submit} class="space-y-6">
                    <div>
                        <label for="name" class="block text-sm font-medium text-gray-300 mb-1">Name</label>
                        <input
                            id="name"
                            type="text"
                            bind:value={$form.name}
                            class="block w-full rounded-md border border-gray-700 bg-gray-800 text-gray-100 px-3 py-2 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm"
                        />
                        {#if $form.errors.name}
                            <p class="mt-1 text-sm text-red-400">{$form.errors.name}</p>
                        {/if}
                    </div>

                    <div>
                        <label for="email" class="block text-sm font-medium text-gray-300 mb-1">Email</label>
                        <input
                            id="email"
                            type="email"
                            bind:value={$form.email}
                            class="block w-full rounded-md border border-gray-700 bg-gray-800 text-gray-100 px-3 py-2 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm"
                        />
                        {#if $form.errors.email}
                            <p class="mt-1 text-sm text-red-400">{$form.errors.email}</p>
                        {/if}
                    </div>

                    <div>
                        <label for="message" class="block text-sm font-medium text-gray-300 mb-1">Message</label>
                        <textarea
                            id="message"
                            bind:value={$form.message}
                            rows="6"
                            class="block w-full rounded-md border border-gray-700 bg-gray-800 text-gray-100 px-3 py-2 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm"
                        ></textarea>
                        {#if $form.errors.message}
                            <p class="mt-1 text-sm text-red-400">{$form.errors.message}</p>
                        {/if}
                    </div>

                    {#if showCaptcha}
                        <div>
                            <div bind:this={turnstileContainer}></div>
                            {#if $form.errors.cf_turnstile_response}
                                <p class="mt-1 text-sm text-red-400">{$form.errors.cf_turnstile_response}</p>
                            {/if}
                        </div>
                    {/if}

                    <button
                        type="submit"
                        disabled={$form.processing}
                        class="inline-flex items-center rounded-md bg-cyan-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:opacity-50"
                    >
                        {#if $form.processing}
                            Sending...
                        {:else}
                            Send Feedback
                        {/if}
                    </button>
                </form>
            {/if}
        </div>
    </div>
